import "./App.css";
import { Routes, Route, Navigate, useNavigate, useParams } from "react-router-dom";
import { AuthRoute } from "Route/AuthRoute";
import { useEffect, useState, useMemo, lazy, Suspense } from "react";
import { actions } from "redux/User/action";
import { useDispatch } from "react-redux";
import { ToastContainer } from "react-toastify";
import { PrivateRoute } from "Route/PrivateRoute";
import { actions as playersAction } from "redux/Player/action";
import { actions as courseActions } from "redux/Course/action";
import { useSelector } from "react-redux";
import AppShell from "components/shell/AppShell";
import SessionTimeoutWarning from "components/SessionTimeoutWarning/SessionTimeoutWarning";
import { isTokenValid, isRefreshTokenValid, clearAuthTokens, getDecodedToken } from "utils/tokenUtils";
import { hasHandoffCode, takeHandoffCode, exchangeHandoffCode } from "utils/handoff";

// Login is the landing screen for unauthenticated users — keep it eager so it
// renders instantly with no loading flash. Every other page is code-split with
// React.lazy so it only downloads when its route is visited, shrinking the
// initial bundle dramatically.
import Login from "pages/login/Login";

const CoursesPage = lazy(() => import("pages/Courses/CoursesPage"));
const CourseEditor = lazy(() => import("pages/Courses/CourseEditor"));
const NewCourse = lazy(() => import("pages/Courses/NewCourse"));
const Players = lazy(() => import("pages/Players/Players"));
const CommentRequests = lazy(() => import("pages/CommentRequests/CommentRequests"));
const AdminDashboard = lazy(() => import("pages/Dashboard/AdminDashboard"));
const ClubAdminDashboard = lazy(() =>
  import("pages/Dashboard/ClubAdminDashboard")
);
const Account = lazy(() => import("pages/Account/Account"));
const ArchivedOrganisations = lazy(() => import("pages/Archived/ArchivedOrganisations"));
const ForgotPassword = lazy(() =>
  import("pages/ForgotPassword/ForgotPassword")
);
const ResetPassword = lazy(() => import("pages/ForgotPassword/ResetPassword"));
const Blocked = lazy(() => import("pages/Archived/Blocked"));
const CourseLibrary = lazy(() => import("pages/CourseLibrary/CourseLibrary"));
const LibraryCourseEditor = lazy(() => import("pages/CourseLibrary/LibraryCourseEditor"));
const AllCoursesPage = lazy(() => import("pages/CourseLibrary/AllCoursesPage"));
const GradingsPage = lazy(() => import("pages/CourseLibrary/GradingsPage"));
const UserProfile = lazy(() => import("pages/ProfileFields/PlatformFieldsPage"));
const OrganizationUserProfile = lazy(() => import("pages/ProfileFields/ProfileFieldsPage"));
const PlayerCategories = lazy(() =>
  import("pages/PlayerCategories/PlayerCategories")
);
const CVSectionSettingsPage = lazy(() =>
  import("pages/CVSectionSettings/CVSectionSettingsPage")
);
const SkillCatalogue = lazy(() => import("pages/SkillCatalogue/SkillCatalogue"));
const OrgCareers = lazy(() => import("pages/OrgCareers/OrgCareers"));
const MetricCatalogue = lazy(() =>
  import("pages/MetricCatalogue/MetricCatalogue")
);
const Theme = lazy(() => import("pages/Theme/Theme"));
const WorkExperienceSummary = lazy(() =>
  import("pages/WorkExperienceSummary/WorkExperienceSummary")
);
const IndividualAccounts = lazy(() =>
  import("pages/IndividualAccounts/IndividualAccounts")
);
const Template4Stats = lazy(() => import("pages/Template4Stats/Template4Stats"));
const NotFound = lazy(() => import("pages/NotFound/NotFound"));
const CvAccess = lazy(() => import("pages/CvAccess/CvAccess"));
const OrgStorage = lazy(() => import("pages/OrgStorage/OrgStorage"));
const OrgStorageTab = lazy(() => import("pages/OrgStorage/OrgStorageTab"));
const ContentReview = lazy(() => import("pages/ContentReview/ContentReview"));
const Support = lazy(() => import("pages/Support/Support"));
const SystemAdmins = lazy(() => import("pages/SystemAdmins/SystemAdmins"));
const OrgReport = lazy(() => import("pages/OrgReport/OrgReport"));
const ActivityLog = lazy(() => import("pages/ActivityLog/ActivityLog"));
const CareerLists = lazy(() => import("pages/CareerLists/CareerLists"));
const ClubSettings = lazy(() => import("pages/ClubSettings/ClubSettings"));
const OrgOverview = lazy(() => import("pages/OrgOverview/OrgOverview"));
const TutorHomepage = lazy(() => import("pages/TutorHomepage/TutorHomepage"));
const LearnersList = lazy(() => import("pages/Learners/LearnersList"));
const LearnerProfile = lazy(() => import("pages/Learners/LearnerProfile"));
const LearnersStatusList = lazy(() => import("pages/Learners/LearnersStatusList"));
const NewLearner = lazy(() => import("pages/Learners/NewLearner"));
const ImportLearners = lazy(() => import("pages/Learners/ImportLearners"));

// Old course links now live under /organisation/courses.
function LegacyCourseRedirect() {
  const { courseId } = useParams();
  return <Navigate to={`/organisation/courses/${courseId}`} replace />;
}

// Old library links now live under /courses.
function LegacyTypeRedirect() {
  const { instituteTypeId } = useParams();
  return <Navigate to={`/courses?type=${instituteTypeId}`} replace />;
}
function LegacyLibraryCourseRedirect() {
  const { courseId } = useParams();
  return <Navigate to={`/courses/course/${courseId}`} replace />;
}

// Lightweight fallback shown while a lazily-loaded route chunk downloads.
function PageLoader() {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "60vh",
      }}
    >
      <div className="spinner" />
    </div>
  );
}

function App() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.user);
  const [token, setToken] = useState(() => localStorage.getItem("TOKEN"));

  // Opened from Careers with #handoff=: swap the one-time code for a session
  // before the routes decide whether to show the login page.
  const [handoffPending, setHandoffPending] = useState(hasHandoffCode);
  useEffect(() => {
    if (!handoffPending) return;
    const code = takeHandoffCode();
    if (localStorage.getItem("TOKEN")) {
      setHandoffPending(false);
      return;
    }
    exchangeHandoffCode(code)
      .then(({ token: newToken, refreshToken }) => {
        localStorage.setItem("TOKEN", newToken);
        localStorage.setItem("REFRESH_TOKEN", refreshToken);
        setToken(newToken);
      })
      .catch(() => {})
      .finally(() => setHandoffPending(false));
    // Runs once, on load.
    // eslint-disable-next-line
  }, []);

  // Logout Function. Goes through the saga so the server ends the session too.
  const logoutUser = () => {
    dispatch(actions.userLogout(navigate));
  };

  // Check token validity and handle expiration
  useEffect(() => {
    const currentToken = localStorage.getItem("TOKEN");
    
    if (currentToken !== token) {
      setToken(currentToken);
    }

    if (!currentToken) {
      if (token) {
        setToken(null);
      }
      return;
    }

    if (!isTokenValid()) {
      // Access Token Expired, Check Refresh Token
      if (isRefreshTokenValid()) {
        const refreshToken = localStorage.getItem("REFRESH_TOKEN");
        // Refresh Token is Valid, Get New Access Token
        dispatch(actions.userRefreshToken(refreshToken, navigate));
      } else {
        // Both Tokens Expired, Logout
        clearAuthTokens();
        setToken(null);
        if (window.location.pathname !== "/auth/login") {
          navigate("/auth/login");
        }
      }
    }
  }, [token, dispatch, navigate]);

  // Get decoded token safely - memoized to prevent unnecessary recalculations
  const decoded = useMemo(() => {
    if (!token || !isTokenValid()) return null;
    return getDecodedToken();
  }, [token]);
  const decodedRole = decoded?.role;
  const isAdminRole = decodedRole === "admin";
  const isTutorOrClubAdmin =
    decodedRole === "tutor" || decodedRole === "clubAdmin";

  const { brandTheme } = useSelector((state) => state.brand);
  const randomNumber = (min, max) => {
    return Math.floor(Math.random() * (999 - 100 + 1)) + 100;
  };

  useEffect(() => {
    if (user?.designation === "Routes Super Admin") {
      const root = document.documentElement;
      root.style.setProperty("--primary-color", "#006B01");
      root.style.setProperty("--secondary-color", "#006B01");
      root.style.setProperty("--hover-color", "#006B01");
    }

    if (brandTheme && !brandTheme?.organizationId && brandTheme?.themeColors) {
      localStorage.setItem("brandTheme", JSON.stringify(brandTheme));
    }

    if (
      brandTheme &&
      brandTheme?.themeColors &&
      user?.designation !== "Routes Super Admin"
    ) {
      const brandThemeLocal = JSON.parse(localStorage.getItem("brandTheme"));
      const root = document.documentElement;
      root.style.setProperty(
        "--primary-color",
        brandTheme
          ? brandTheme?.themeColors?.primary
          : brandThemeLocal?.themeColors?.primary
      );
      root.style.setProperty(
        "--secondary-color",
        brandTheme
          ? brandTheme?.themeColors?.secondary
          : brandThemeLocal?.themeColors?.secondary
      );
      root.style.setProperty(
        "--hover-color",
        brandTheme
          ? brandTheme?.themeColors?.hover
          : brandThemeLocal?.themeColors?.hover
      );
    } else {
      const root = document.documentElement;
      root.style.setProperty("--primary-color", "#006B01");
      root.style.setProperty("--secondary-color", "#006B01");
      root.style.setProperty("--hover-color", "#006B01");
    }

    if (brandTheme && brandTheme?.logos?.favicon) {
      const favicon = document.querySelector('link[rel="icon"]');

      if (favicon) {
        favicon.href = `${brandTheme?.logos?.favicon}?${randomNumber(1, 1000)}`;
      } else {
        const newFavicon = document.createElement("link");
        newFavicon.rel = "icon";
        newFavicon.href = `${brandTheme?.logos?.favicon}?${randomNumber(
          1,
          1000
        )}`;
        document.head.appendChild(newFavicon);
      }
    }

    if (brandTheme && brandTheme?.brandName) {
      document.title = `${
        brandTheme?.brandName
          ? brandTheme?.brandName + " - Control Centre"
          : "Routes - Control Centre"
      }`;
    }
  }, [brandTheme]);

  useEffect(() => {
    if (!token || !isTokenValid()) return;

    const decodedToken = getDecodedToken();
    if (!decodedToken) return;

    // Sponsors must use the Careers app, not Control Centre — clear token and redirect
    if (decodedToken?.role === "sponsor") {
      clearAuthTokens();
      setToken(null);
      const careersUrl = process.env.REACT_APP_CAREERS_URL || "https://talent.routes-app.com";
      window.location.href = careersUrl;
      return;
    }

    if (
      decodedToken?.role !== "admin" &&
      decodedToken?.role !== "sponsor" &&
      decodedToken?.isDelete
    ) {
      localStorage.removeItem("TOKEN");
      setToken(null);
      navigate("/auth/account/blocked");
      return;
    }

    dispatch(
      actions.userDetail(
        decodedToken?.role === "admin" ? "admin" : "tutor",
        navigate
      )
    );
    dispatch(playersAction.getPlayerStatus());
    // Super admins need a specific org's categories, which each org page fetches;
    // an unscoped fetch here would overwrite them.
    if (decodedToken?.role !== "admin") {
      dispatch(playersAction.getPlayerCategory());
    }
    dispatch(courseActions.getInstitutions());
    // navigate changes on every route change; this should only run per token.
    // eslint-disable-next-line
  }, [token, dispatch]);

  if (handoffPending) return <PageLoader />;

  return (
    <>
      <ToastContainer />
      {/* <BrowserRouter> */}

      <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route element={<PrivateRoute />}>
        <Route element={<AppShell />}>
          <Route
            path="/"
            element={
              (isTutorOrClubAdmin && (
                <TutorHomepage />
              )) ||
              (isAdminRole && <AdminDashboard />)
            }
          />
          <Route
            path="/organisation/courses"
            element={
              isTutorOrClubAdmin ? (
                <CoursesPage />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/organisation/courses/new"
            element={decoded?.role === "clubAdmin" ? <NewCourse /> : <Navigate to="/organisation/courses" />}
          />
          <Route
            path="/organisation/courses/:courseId"
            element={decoded?.role === "clubAdmin" ? <CourseEditor /> : <Navigate to="/organisation/courses" />}
          />
          <Route
            path="/organisation/mentors"
            element={
              decoded?.role === "clubAdmin" ? (
                <ClubAdminDashboard />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/players"
            element={
              decoded?.role === "admin" ? (
                <Players />
              ) : (
                <Navigate to="/organisation/users" />
              )
            }
          />
          <Route
            path="/organisation/users"
            element={
              isTutorOrClubAdmin || isAdminRole ? (
                <LearnersList />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/organisation/users/new"
            element={
              isTutorOrClubAdmin || isAdminRole ? (
                <NewLearner />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/organisation/users/upload"
            element={
              isTutorOrClubAdmin || isAdminRole ? (
                <ImportLearners />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/organisation/users/:userId"
            element={
              isTutorOrClubAdmin || isAdminRole ? (
                <LearnerProfile />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/players/alumni"
            element={
              decoded?.role === "admin" || decoded?.role === "clubAdmin" ? (
                <LearnersStatusList key="alumni" status="alumni" />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/players/inactive"
            element={
              decoded?.role === "admin" || decoded?.role === "clubAdmin" ? (
                <LearnersStatusList key="inactive" status="inactive" />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/comment-requests"
            element={
              decoded?.role === "tutor" || decoded?.role === "clubAdmin" ? (
                <CommentRequests />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/club/comment-requests/:id"
            element={decoded?.role === "admin" ? <CommentRequests /> : <Navigate to="/" />}
          />
          <Route
            path="/master"
            element={<Navigate to="/players" replace />}
          />
          <Route
            path="/profile"
            element={
              isTutorOrClubAdmin || isAdminRole ? (
                <Account />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/theme"
            element={
              // decoded?.role === "tutor" ||
              // decoded?.role === "clubAdmin" ||

              decoded?.role === "admin" ? <Theme /> : <Navigate to="/" />
            }
          />

          <Route
            path="/clubs"
            element={
              decoded?.role === "admin" ? (
                <AdminDashboard />
              ) : (
                <Navigate to="/" />
              )
            }
          />

          <Route
            path="/clubs/archived"
            element={
              decoded?.role === "admin" ? <ArchivedOrganisations /> : <Navigate to="/" />
            }
          />

          <Route
            path="/individual-accounts"
            element={
              decoded?.role === "admin" ? <IndividualAccounts /> : <Navigate to="/" />
            }
          />

          <Route
            path="/club/users/:id"
            element={
              decoded?.role === "admin" ? (
                <LearnersList />
              ) : (
                <Navigate to="/" />
              )
            }
          />

          <Route
            path="/club/users/:id/new"
            element={decoded?.role === "admin" ? <NewLearner /> : <Navigate to="/" />}
          />

          <Route
            path="/club/users/:id/import"
            element={decoded?.role === "admin" ? <ImportLearners /> : <Navigate to="/" />}
          />

          <Route
            path="/club/users/:id/:userId"
            element={
              decoded?.role === "admin" ? (
                <LearnerProfile />
              ) : (
                <Navigate to="/" />
              )
            }
          />

          <Route
            path="/club/report/:id"
            element={
              isAdminRole || isTutorOrClubAdmin ? (
                <OrgReport />
              ) : (
                <Navigate to="/" />
              )
            }
          />

          <Route
            path="/activity"
            element={decoded?.role === "admin" ? <ActivityLog /> : <Navigate to="/" />}
          />

          <Route
            path="/career-lists"
            element={decoded?.role === "admin" ? <CareerLists /> : <Navigate to="/" />}
          />

          <Route
            path="/club/settings/:id"
            element={
              decoded?.role === "admin" ? (
                <ClubSettings />
              ) : (
                <Navigate to="/" />
              )
            }
          />

          <Route
            path="/club/alumni/:id"
            element={
              decoded?.role === "admin" || decoded?.role === "clubAdmin" ? (
                <LearnersStatusList key="alumni" status="alumni" />
              ) : (
                <Navigate to="/" />
              )
            }
          />

          <Route
            path="/club/inactive/:id"
            element={
              decoded?.role === "admin" || decoded?.role === "clubAdmin" ? (
                <LearnersStatusList key="inactive" status="inactive" />
              ) : (
                <Navigate to="/" />
              )
            }
          />

          <Route
            path="/club/archived/:id"
            element={
              decoded?.role === "admin" || decoded?.role === "clubAdmin" ? (
                <LearnersStatusList key="archived" status="archived" />
              ) : (
                <Navigate to="/" />
              )
            }
          />

          <Route
            path="/tutors/archived"
            element={
              decoded?.role === "clubAdmin" || decoded?.role === "admin" ? (
                <ClubAdminDashboard archived />
              ) : (
                <Navigate to="/" />
              )
            }
          />

          <Route
            path="/players/archived"
            element={
              decoded?.role === "admin" || decoded?.role === "clubAdmin" ? (
                <LearnersStatusList key="archived" status="archived" />
              ) : (
                <Navigate to="/" />
              )
            }
          />

          {/* Work Experience Summary Routes */}
          <Route
            path="/work-experience-summary"
            element={
             isTutorOrClubAdmin || isAdminRole ? (
                <WorkExperienceSummary />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/work-experience-summary/:id"
            element={
              isTutorOrClubAdmin || isAdminRole ? (
                <WorkExperienceSummary />
              ) : (
                <Navigate to="/" />
              )
            }
          />

          <Route
            path="/club/overview/:id"
            element={
              decoded?.role === "admin" ? <OrgOverview /> : <Navigate to="/" />
            }
          />
          <Route
            path="/club/info/:id"
            element={
              decoded?.role === "admin" ? (
                <ClubAdminDashboard />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/club/dashboard/:id"
            element={
              decoded?.role === "admin" ? <CoursesPage /> : <Navigate to="/" />
            }
          />
          <Route
            path="/club/dashboard/:id/course/new"
            element={decoded?.role === "admin" ? <NewCourse /> : <Navigate to="/" />}
          />
          <Route
            path="/club/dashboard/:id/course/:courseId"
            element={decoded?.role === "admin" ? <CourseEditor /> : <Navigate to="/" />}
          />
          <Route
            path="/course/:courseId"
            element={
              isTutorOrClubAdmin ? <LegacyCourseRedirect /> : <Navigate to="/" />
            }
          />

          <Route
            path="/courses"
            element={decoded?.designation === "SystemAdmin" ? <CourseLibrary /> : <Navigate to="/" />}
          />

          <Route
            path="/courses/qualification-type/:instituteTypeId"
            element={decoded?.role === "admin" ? <LegacyTypeRedirect /> : <Navigate to="/" />}
          />

          <Route
            path="/courses/course/new"
            element={decoded?.role === "admin" ? <LibraryCourseEditor /> : <Navigate to="/" />}
          />

          <Route
            path="/courses/course/:courseId"
            element={decoded?.role === "admin" ? <LibraryCourseEditor /> : <Navigate to="/" />}
          />

          <Route
            path="/all-courses"
            element={decoded?.designation === "SystemAdmin" ? <AllCoursesPage /> : <Navigate to="/" />}
          />
          <Route path="/gradings" element={decoded?.role === "admin" ? <GradingsPage /> : <Navigate to="/" />} />
          <Route
            path="/course/update/:courseId"
            element={decoded?.role === "admin" ? <LegacyLibraryCourseRedirect /> : <Navigate to="/" />}
          />

          <Route
            path="/common-user-profile"
            element={
              decoded?.designation === "SystemAdmin" ? (
                <UserProfile />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/organisation/common-user-profile"
            element={
              decoded?.role === "clubAdmin" ? (
                <OrganizationUserProfile />
              ) : (
                <Navigate to="/" />
              )
            }
          />

          <Route
            path="/organisation/player-categories"
            element={
              decoded?.role === "clubAdmin" ? (
                <PlayerCategories />
              ) : (
                <Navigate to="/" />
              )
            }
          />

          <Route
            path="/organisation/cv-section-settings"
            element={
              decoded?.role === "clubAdmin" ? (
                <CVSectionSettingsPage />
              ) : (
                <Navigate to="/" />
              )
            }
          />

          <Route
            path="/club/careers/:id"
            element={decoded?.role === "admin" ? <OrgCareers /> : <Navigate to="/" />}
          />
          <Route
            path="/organisation/careers"
            element={
              decoded?.role === "clubAdmin" ? (
                <OrgCareers />
              ) : (
                <Navigate to="/" />
              )
            }
          />

          <Route
            path="/organisation/skill-catalogue"
            element={
              decoded?.role === "clubAdmin" ? (
                <SkillCatalogue />
              ) : (
                <Navigate to="/" />
              )
            }
          />

          <Route
            path="/organisation/metric-catalogue"
            element={
              decoded?.role === "clubAdmin" ? (
                <MetricCatalogue />
              ) : (
                <Navigate to="/" />
              )
            }
          />

          <Route
            path="/template4-stats/:fieldId"
            element={
              decoded?.role === "clubAdmin" || decoded?.role === "admin" ? (
                <Template4Stats />
              ) : (
                <Navigate to="/" />
              )
            }
          />

          <Route
            path="/club/storage/:id"
            element={decoded?.role === "admin" ? <OrgStorageTab /> : <Navigate to="/" />}
          />

          <Route
            path="/org-storage"
            element={
              decoded?.role === "admin" ? <OrgStorage /> : <Navigate to="/" />
            }
          />

          <Route path="/support" element={<Support />} />
          <Route
            path="/system-admins"
            element={
              decoded?.designation === "SystemAdmin" ? <SystemAdmins /> : <Navigate to="/" />
            }
          />

          <Route
            path="/club/content-safety/:id"
            element={decoded?.role === "admin" ? <ContentReview /> : <Navigate to="/" />}
          />
          <Route
            path="/content-review"
            element={
              decoded?.role === "admin" || decoded?.role === "clubAdmin" ? (
                <ContentReview />
              ) : (
                <Navigate to="/" />
              )
            }
          />
        </Route>
        </Route>

        {/* Without login route */}
        <Route path="/auth" element={<AuthRoute />}>
          <Route path="login" element={<Login />} />
          <Route path="forgot-password" element={<ForgotPassword />} />
          <Route
            path="password-reset/:userId/:token"
            // path="password-reset"
            element={<ResetPassword />}
          />
          <Route
            path="account/blocked"
            // path="password-reset"
            element={<Blocked />}
          />
        </Route>
        <Route path="/cv-access" element={<CvAccess />} />
        <Route
          path="*"
          element={token ? <NotFound /> : <Navigate to="/auth/login" />}
        />
      </Routes>
      </Suspense>

      {/* </BrowserRouter> */}
      
      {/* Session Timeout Warning */}
      <SessionTimeoutWarning 
        onLogout={logoutUser}
        onExtend={() => {
          // Trigger token refresh
          const refreshToken = localStorage.getItem("REFRESH_TOKEN");
          if (refreshToken && isRefreshTokenValid()) {
            dispatch(actions.userRefreshToken(refreshToken, navigate));
          }
        }}
      />
    </>
  );
}

export default App;